import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { PageTourContext } from "./context";
import { hasSeenPageTour, markPageTourSeen } from "./tourStorage";
import type { PageTourStep, RegisteredPageTour } from "./types";
import { hasOtherDialog, visibleTourSteps } from "./targets";
import { SpotlightTour } from "./SpotlightTour";

interface Registration {
  tour: RegisteredPageTour;
  pathname: string;
}

export function PageTourProvider({
  userId,
  paused = false,
  children,
}: {
  userId: string | undefined;
  paused?: boolean;
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [replay, setReplay] = useState<{
    registration: Registration;
    userId: string;
  } | null>(null);
  const [, setRevision] = useState(0);
  const [customSession, setCustomSession] = useState<{
    registration: Registration;
    userId: string;
  } | null>(null);
  const [availability, setAvailability] = useState<{
    registration: Registration | null;
    steps: readonly PageTourStep[];
    blocked: boolean;
  }>({ registration: null, steps: [], blocked: true });

  const register = useCallback((tour: RegisteredPageTour, path: string) => {
    const next = { tour, pathname: path };
    setRegistration(next);
    return () =>
      setRegistration((current) => (current === next ? null : current));
  }, []);

  const current = registration?.pathname === pathname ? registration : null;
  const seen =
    !!userId && !!current && hasSeenPageTour(userId, current.tour.id);
  const manuallyOpened =
    replay?.registration === current && replay?.userId === userId;
  const pending = !!userId && !!current && (!seen || manuallyOpened);

  // Observe readiness rather than timing a tour against network requests.
  // A page may mount its controls after its registration effect; dialogs can
  // also be fading out after news has been dismissed.
  useEffect(() => {
    if (!current || paused) return;
    let frame = 0;
    const measure = () => {
      const steps =
        current.tour.kind === "spotlight"
          ? visibleTourSteps(current.tour.steps)
          : [];
      const blocked = hasOtherDialog();
      setAvailability((previous) =>
        previous.registration === current &&
        previous.blocked === blocked &&
        previous.steps.length === steps.length &&
        previous.steps.every((step, index) => step === steps[index])
          ? previous
          : { registration: current, steps, blocked }
      );
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class", "hidden"],
    });
    window.addEventListener("resize", schedule);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
    };
  }, [current, paused]);

  const available =
    !!userId &&
    !!current &&
    (current.tour.kind === "custom" ||
      (availability.registration === current && availability.steps.length > 0));
  const customRunning =
    current?.tour.kind === "custom" &&
    customSession?.registration === current &&
    customSession?.userId === userId;
  const open =
    pending &&
    available &&
    !paused &&
    availability.registration === current &&
    (customRunning || !availability.blocked);

  useEffect(() => {
    if (paused) setCustomSession(null);
    else if (
      open &&
      current?.tour.kind === "custom" &&
      userId &&
      !customRunning
    ) {
      setCustomSession({ registration: current, userId });
    }
  }, [paused, open, current, userId, customRunning]);

  const start = useCallback(() => {
    if (current && userId) setReplay({ registration: current, userId });
  }, [current, userId]);
  const close = useCallback(() => {
    if (!current || !userId) return;
    markPageTourSeen(userId, current.tour.id);
    setReplay(null);
    setCustomSession(null);
    setRevision((value) => value + 1);
  }, [current, userId]);

  const value = useMemo(
    () => ({
      register,
      pathname,
      paused,
      tourId: current?.tour.id,
      open,
      pending,
      start,
      close,
      available,
    }),
    [
      register,
      pathname,
      paused,
      current,
      open,
      pending,
      start,
      close,
      available,
    ]
  );

  return (
    <PageTourContext.Provider value={value}>
      {children}
      {open && current?.tour.kind === "spotlight" && (
        <SpotlightTour
          key={`${userId}:${current.pathname}:${current.tour.id}`}
          title={current.tour.title}
          steps={availability.steps}
          onClose={close}
        />
      )}
    </PageTourContext.Provider>
  );
}
